import { Controller } from "@hotwired/stimulus"

// Right-hand sidebar for a Granola meeting note. Opened by a note row on the
// contact profile or in the Network detail pane; the panel itself is mounted
// app-wide (shared/_note_sidebar) so no surface has to host it.
//
// The one thing this does that no other panel on the site does is RESIZE. The
// width lives in a CSS custom property on the panel and is persisted in
// localStorage, so it survives navigation and reloads.
const WIDTH_KEY = "emailpa-note-sidebar-width"

// The panel was built for meeting notes; a ClickUp chip on a task row opens it
// too. (Contact and company chips open /network's docked detail panel
// instead - see detail_modal_controller.) Rather than clone the shell (the
// partial's own comment already calls unifying the shells a worthwhile
// follow-up), the CHROME is per-open — an icon and an aria-label ride along in
// the open detail and default back to the meeting-note wording.
const VIEW_KEY = "emailpa-note-sidebar-view"
const VIEWS = ["sidebar", "drawer", "floating"]
// Where a panel can dock beside the page: the same "split layout" the other
// right-hand panels use (_variables.scss $mq-split-layout). Anywhere else the
// sidebar view is the drawer.
const SPLIT_LAYOUT = window.matchMedia
    ? window.matchMedia("(min-width: 1024px) and (pointer: fine) and (hover: hover), (min-width: 667px) and (max-height: 500px) and (pointer: coarse), (min-width: 768px) and (min-height: 501px) and ((hover: none) or (max-width: 1023.98px))")
    : { matches: true }
const DEFAULT_ICON = "bi-journal-text"
const DEFAULT_LABEL = "Meeting note"

export default class extends Controller {
    static targets = ["panel", "body", "title", "resizer", "icon", "heading", "headSlot", "fullscreen", "windowItem"]
    static values = { minWidth: { type: Number, default: 320 }, maxWidth: { type: Number, default: 1400 } }

    connect() {
        this._onDocClick = (event) => this._maybeCloseOnOutside(event)
        this._onKeydown = (event) => {
            if (event.key !== "Escape") return

            if (this._fullscreen) this._setFullscreen(false)
            else this.close()
        }
        this._onPointerMove = (event) => this._onResizeMove(event)
        this._onPointerUp = () => this._endResize()
        // Cross-surface open hook: a note row dispatches this instead of
        // targeting the controller directly, so rows rendered inside the
        // Network pane (a different Stimulus scope) need no knowledge of where
        // the panel lives.
        this._onOpenNote = (event) => this.openFromDetail(event.detail || {})
        // Same panel, same contract, a name that isn't about notes - both are
        // listened for so callers can use whichever reads honestly.
        this._onOpenPanel = this._onOpenNote
        window.addEventListener("jane:open-note", this._onOpenNote)
        window.addEventListener("jane:open-panel", this._onOpenPanel)
        // Anything inside the panel body can ask for it to be dismissed without
        // reaching into this controller's scope (see company_unlink_controller).
        this._onClosePanel = () => this.close()
        window.addEventListener("jane:close-panel", this._onClosePanel)
        // Messages docked after this: one docked right sidebar at a time, and
        // the one just opened wins (opening THIS one closes Messages first, so
        // by the time this fires for our own change the class is already gone).
        this._onPanelsChanged = () => {
            if (!this.panelTarget.classList.contains("hidden") && this._mode === "sidebar" && SPLIT_LAYOUT.matches &&
                document.documentElement.classList.contains("messages-docked")) this.close()
        }
        window.addEventListener("jane:panels-changed", this._onPanelsChanged)
        // Re-clamp on resize: a width saved on a large monitor must not swallow
        // a smaller window, and one clamped down on a narrow window should be
        // free to grow back. Reads the SAVED width each time rather than the
        // current one, so repeated shrinking doesn't ratchet the preference down.
        this._onWindowResize = () => this._applyWidth(this._savedWidth())
        window.addEventListener("resize", this._onWindowResize)
        this._applyWidth(this._savedWidth())
        // Docked as a sidebar unless the person chose otherwise, like Messages.
        this._applyView(this._savedView())
        this._onHeadDown = (event) => this._startDrag(event)
        if (this.hasPanelTarget) this.panelTarget.querySelector(".note-sidebar-head")?.addEventListener("pointerdown", this._onHeadDown)
    }

    disconnect() {
        document.documentElement.classList.remove("note-docked")
        this._teardownListeners()
        window.removeEventListener("jane:open-note", this._onOpenNote)
        window.removeEventListener("jane:open-panel", this._onOpenPanel)
        window.removeEventListener("jane:close-panel", this._onClosePanel)
        window.removeEventListener("jane:panels-changed", this._onPanelsChanged)
        window.removeEventListener("resize", this._onWindowResize)
        this._endResize()
    }

    // Action target for a note row: data-note-url and data-note-title carry
    // everything needed.
    open(event) {
        if (event) event.preventDefault()
        const el = event?.currentTarget
        if (!el) return

        this.openFromDetail({ url: el.dataset.noteUrl, title: el.dataset.noteTitle })
    }

    openFromDetail({ url, title, icon, label }) {
        if (!url || !this.hasPanelTarget) return

        if (this.hasTitleTarget && title) this.titleTarget.textContent = title
        this._applyChrome(icon, label)
        this._show()
        this._load(url)
    }

    // Swap the head icon and the panel's accessible name for whatever this open
    // is showing, falling back to the meeting-note defaults so existing callers
    // (which pass neither) are unchanged.
    _applyChrome(icon, label) {
        const name = label || DEFAULT_LABEL
        this.panelTarget.setAttribute("aria-label", name)
        if (!this.hasIconTarget) return

        this.iconTarget.className = `bi ${icon || DEFAULT_ICON} note-sidebar-head-icon`
    }

    close() {
        if (!this.hasPanelTarget) return

        this._setFullscreen(false)
        this.panelTarget.classList.add("hidden")
        this.panelTarget.setAttribute("aria-hidden", "true")
        document.body.classList.remove("note-sidebar-active")
        this._syncDocked()
        this._teardownListeners()
    }

    _show() {
        this.panelTarget.classList.remove("hidden")
        this.panelTarget.setAttribute("aria-hidden", "false")
        document.body.classList.add("note-sidebar-active")
        // One docked right sidebar at a time: a docked Messages steps aside.
        if (this._mode === "sidebar" && SPLIT_LAYOUT.matches && document.documentElement.classList.contains("messages-docked")) {
            window.dispatchEvent(new CustomEvent("jane:messages-close"))
        }
        this._syncDocked()
        // Deferred so the click that opened the panel doesn't immediately close
        // it via the outside-click listener.
        window.requestAnimationFrame(() => {
            document.addEventListener("click", this._onDocClick)
            document.addEventListener("keydown", this._onKeydown)
        })
    }

    async _load(url) {
        if (!this.hasBodyTarget) return

        // A stale in-flight response must never paint over a note the user has
        // since clicked, so every load stamps its own token.
        const token = Symbol("load")
        this._loadToken = token
        this._clearHead()
        this.bodyTarget.innerHTML = '<p class="note-sidebar-empty">Loading…</p>'
        try {
            const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            if (!response.ok) throw new Error(`load failed: ${response.status}`)
            const html = await response.text()
            if (this._loadToken !== token) return

            this.bodyTarget.innerHTML = html
            this.bodyTarget.scrollTop = 0
            this._adoptHead()
        } catch (_error) {
            if (this._loadToken !== token) return

            this.bodyTarget.innerHTML = '<p class="note-sidebar-empty">That note could not be loaded.</p>'
        }
    }

    // A fragment may bring its own head bar as <template data-panel-head>: clone it
    // into the panel's head and let the plain heading step aside. Cloned (not
    // moved) so Stimulus connects the controllers inside it where they now live.
    _adoptHead() {
        if (!this.hasHeadSlotTarget) return

        this._syncPageLink()
        const template = this.bodyTarget.querySelector("template[data-panel-head]")
        if (!template) return

        this.headSlotTarget.appendChild(template.content.cloneNode(true))
        this.headSlotTarget.hidden = false
        if (this.hasHeadingTarget) this.headingTarget.hidden = true
    }

    // Full screen over whichever view it is in, as on Messages: Escape or the same
    // button brings it back to where it was.
    toggleFullscreen(event) {
        if (event) event.preventDefault()
        this._setFullscreen(!this._fullscreen)
    }

    _setFullscreen(on) {
        if (!this.hasPanelTarget || Boolean(this._fullscreen) === on) return

        this._fullscreen = on
        this.panelTarget.classList.toggle("note-sidebar--fullscreen", on)
        if (this.hasFullscreenTarget) {
            this.fullscreenTarget.setAttribute("aria-pressed", String(on))
            this.fullscreenTarget.title = on ? "Exit full screen" : "Full screen"
            this.fullscreenTarget.setAttribute("aria-label", this.fullscreenTarget.title)
            const icon = this.fullscreenTarget.querySelector("i")
            if (icon) icon.className = `bi ${on ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`
        }
        this._syncDocked()
    }

    // Content with a page of its own (a document) says where, with
    // data-panel-page-url; the three-dot menu then offers it in a window of its own.
    _syncPageLink() {
        this._pageUrl = this.hasBodyTarget ? this.bodyTarget.querySelector("[data-panel-page-url]")?.dataset.panelPageUrl : null
        this.windowItemTargets.forEach((item) => { item.hidden = !this._pageUrl })
    }

    openInWindow(event) {
        if (event) event.preventDefault()
        if (!this._pageUrl) return

        window.open(this._pageUrl, "_blank", "noopener,noreferrer,popup,width=1180,height=860")
    }

    _clearHead() {
        this._pageUrl = null
        this.windowItemTargets.forEach((item) => { item.hidden = true })
        if (!this.hasHeadSlotTarget) return

        this.headSlotTarget.replaceChildren()
        this.headSlotTarget.hidden = true
        if (this.hasHeadingTarget) this.headingTarget.hidden = false
    }

    // --- resize ---

    startResize(event) {
        if (!this.hasPanelTarget) return

        event.preventDefault()
        this._resizing = true
        this._startX = event.clientX
        this._startWidth = this.panelTarget.getBoundingClientRect().width
        document.body.classList.add("note-sidebar-resizing")
        window.addEventListener("pointermove", this._onPointerMove)
        window.addEventListener("pointerup", this._onPointerUp)
        window.addEventListener("pointercancel", this._onPointerUp)
    }

    _onResizeMove(event) {
        if (!this._resizing) return

        // Dragging left (a smaller clientX) widens the panel, since it is
        // anchored to the right edge.
        this._applyWidth(this._startWidth + (this._startX - event.clientX))
    }

    _endResize() {
        if (!this._resizing) return

        this._resizing = false
        document.body.classList.remove("note-sidebar-resizing")
        window.removeEventListener("pointermove", this._onPointerMove)
        window.removeEventListener("pointerup", this._onPointerUp)
        window.removeEventListener("pointercancel", this._onPointerUp)
        this._saveWidth(this.panelTarget.getBoundingClientRect().width)
    }

    // Keyboard resize, so the handle isn't mouse-only.
    resizeByKey(event) {
        const step = event.shiftKey ? 64 : 16
        const current = this.panelTarget.getBoundingClientRect().width
        let next = null
        if (event.key === "ArrowLeft") next = current + step
        else if (event.key === "ArrowRight") next = current - step
        else if (event.key === "Home") next = this.maxWidthValue
        else if (event.key === "End") next = this.minWidthValue
        if (next === null) return

        event.preventDefault()
        this._applyWidth(next)
        this._saveWidth(this.panelTarget.getBoundingClientRect().width)
    }

    resetWidth(event) {
        if (event) event.preventDefault()
        this._applyWidth(420)
        this._saveWidth(420)
    }

    _applyWidth(width) {
        if (!this.hasPanelTarget || !width) return

        const clamped = this._clampWidth(width)
        this.panelTarget.style.setProperty("--note-sidebar-width", `${clamped}px`)
        // On the page too, so a docked panel can be given room (see _note_sidebar.scss).
        document.documentElement.style.setProperty("--note-dock-width", `${clamped}px`)
    }

    // --- view as: sidebar / drawer / floating ---

    setView(event) {
        const view = event.currentTarget.dataset.view
        this._applyView(view)
        try { window.localStorage.setItem(VIEW_KEY, view) } catch (_error) { /* private mode */ }
    }

    _savedView() {
        try {
            const saved = window.localStorage.getItem(VIEW_KEY)
            return VIEWS.includes(saved) ? saved : "sidebar"
        } catch (_error) {
            return "sidebar"
        }
    }

    _applyView(view) {
        if (!this.hasPanelTarget) return

        const mode = VIEWS.includes(view) ? view : "sidebar"
        this._mode = mode
        VIEWS.forEach((m) => this.panelTarget.classList.toggle(`note-sidebar--${m}`, m === mode))
        // Leave no drag offset behind when changing view.
        ;["top", "left", "right", "transform"].forEach((prop) => { this.panelTarget.style[prop] = "" })
        this.element.querySelectorAll(".note-sidebar-view .assistant-header-menu-item[data-view]").forEach((item) => {
            item.classList.toggle("is-active", item.dataset.view === mode)
        })
        this._syncDocked()
    }

    // html.note-docked makes the page room for the sidebar and tucks the page's
    // own right sidebar away (one at a time); see _note_sidebar.scss.
    _syncDocked() {
        if (!this.hasPanelTarget) return

        const docked = this._mode === "sidebar" && !this._fullscreen && SPLIT_LAYOUT.matches && !this.panelTarget.classList.contains("hidden")
        document.documentElement.classList.toggle("note-docked", docked)
        window.dispatchEvent(new CustomEvent("jane:panels-changed"))
    }

    // The floating window is dragged by its header.
    _startDrag(event) {
        if (!this.panelTarget.classList.contains("note-sidebar--floating")) return
        if (event.target.closest("button, a, input")) return

        const rect = this.panelTarget.getBoundingClientRect()
        const offX = event.clientX - rect.left
        const offY = event.clientY - rect.top
        this.panelTarget.style.transform = "none"
        this.panelTarget.style.right = "auto"
        this.panelTarget.style.left = `${rect.left}px`
        this.panelTarget.style.top = `${rect.top}px`
        const move = (ev) => {
            this.panelTarget.style.left = `${ev.clientX - offX}px`
            this.panelTarget.style.top = `${ev.clientY - offY}px`
        }
        const up = () => {
            document.removeEventListener("pointermove", move)
            document.removeEventListener("pointerup", up)
        }
        document.addEventListener("pointermove", move)
        document.addEventListener("pointerup", up)
    }

    // Never wider than the viewport allows, so a width saved on a big monitor
    // doesn't swallow a laptop screen. An unusable innerWidth (0 before layout
    // settles, and in some embedded/headless viewports) is ignored rather than
    // trusted — clamping to it would pin the panel to its minimum for the rest
    // of the page's life. The chat keeps at least CHAT_ROOM to its left: the panel
    // can be dragged wide (its content stays centred at a readable width, as on
    // Claude and ChatGPT) but never so wide the conversation disappears.
    _clampWidth(width) {
        const viewport = window.innerWidth
        const ceiling = viewport > this.minWidthValue
            ? Math.min(this.maxWidthValue, Math.max(this.minWidthValue, viewport - 440))
            : this.maxWidthValue
        return Math.round(Math.min(ceiling, Math.max(this.minWidthValue, width)))
    }

    _savedWidth() {
        try {
            return Number(window.localStorage.getItem(WIDTH_KEY)) || 420
        } catch (_error) {
            return 420
        }
    }

    _saveWidth(width) {
        try {
            window.localStorage.setItem(WIDTH_KEY, String(Math.round(width)))
        } catch (_error) {
            /* localStorage unavailable — the panel still resizes for this view */
        }
    }

    // --- listeners ---

    _maybeCloseOnOutside(event) {
        if (!this.hasPanelTarget || this.panelTarget.classList.contains("hidden")) return
        if (this.panelTarget.contains(event.target)) return
        // A docked sidebar is part of the page: clicking the page doesn't close it.
        if (this._mode === "sidebar" && SPLIT_LAYOUT.matches) return
        // A click on another note row should swap the note, not close the panel
        // and reopen it (which would flash the loading state twice). Rows that
        // open the panel by event (a mirrored ClickUp task) count as note rows.
        if (event.target.closest?.("[data-note-url], [data-panel-row]")) return

        this.close()
    }

    _teardownListeners() {
        document.removeEventListener("click", this._onDocClick)
        document.removeEventListener("keydown", this._onKeydown)
    }
}
